import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Sun, Sunset, Moon, Lock, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const SLOT_META = {
  morning:   { label: "Morning",   range: "8 AM – 12 PM",  icon: Sun },
  afternoon: { label: "Afternoon", range: "12 PM – 4 PM", icon: Sunset },
  evening:   { label: "Evening",   range: "4 PM – 6 PM",  icon: Moon },
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const LOOKAHEAD_DAYS = 60;

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const stripTime = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export default function BookingCalendar({ selectedDate, selectedTime, onChange }) {
  const today = stripTime(new Date());
  const minBookableDate = new Date(today);
  minBookableDate.setDate(minBookableDate.getDate() + 1); // booking starts tomorrow
  const maxBookableDate = new Date(today);
  maxBookableDate.setDate(maxBookableDate.getDate() + LOOKAHEAD_DAYS);

  const [viewMonth, setViewMonth] = useState(() => {
    if (selectedDate) {
      const [y, m] = selectedDate.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [availability, setAvailability] = useState({ taken: {} });
  const [loading, setLoading] = useState(false);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const fromIso = toIso(today);
      const toEnd = toIso(maxBookableDate);
      const res = await api.get(`/availability?from_date=${fromIso}&to_date=${toEnd}`);
      setAvailability(res.data || { taken: {} });
    } catch {
      setAvailability({ taken: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAvailability(); }, []);

  const monthDays = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const lastDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const leadingBlanks = first.getDay();
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    }
    return cells;
  }, [viewMonth]);

  const canGoPrev = useMemo(() => {
    const prevMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1);
    return prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1);
  }, [viewMonth, today]);
  const canGoNext = useMemo(() => {
    const nextMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
    return nextMonth <= new Date(maxBookableDate.getFullYear(), maxBookableDate.getMonth(), 1);
  }, [viewMonth, maxBookableDate]);

  const taken = availability.taken || {};

  const dateState = (date) => {
    if (!date) return "blank";
    const d = stripTime(date);
    if (d < minBookableDate) return "past";
    if (d > maxBookableDate) return "future";
    if (d.getDay() === 0) return "closed";
    const iso = toIso(d);
    const dayTaken = taken[iso];
    const fullyBooked = dayTaken && dayTaken.morning && dayTaken.afternoon && dayTaken.evening;
    if (fullyBooked) return "full";
    if (dayTaken && (dayTaken.morning || dayTaken.afternoon || dayTaken.evening)) return "partial";
    return "open";
  };

  const handleDateClick = (date) => {
    const st = dateState(date);
    if (["blank", "past", "future", "closed", "full"].includes(st)) return;
    onChange({ date: toIso(date), time: null });
  };

  const handleSlotClick = (slot) => {
    if (!selectedDate) return;
    if (taken[selectedDate]?.[slot]) return;
    onChange({ date: selectedDate, time: slot });
  };

  const monthLabel = viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div data-testid="booking-calendar" className="rounded-2xl border-2 border-[#dcd5c6] bg-white p-4 md:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => canGoPrev && setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          disabled={!canGoPrev}
          className="p-2 rounded-lg hover:bg-[var(--brand-cream-soft)] disabled:opacity-30 disabled:cursor-not-allowed transition"
          aria-label="Previous month"
          data-testid="calendar-prev"
        >
          <ChevronLeft className="w-5 h-5 text-[var(--brand-teal)]" />
        </button>
        <div className="flex items-center gap-2">
          <h3 className="font-extrabold text-[var(--brand-teal)] text-base md:text-lg" style={{ fontFamily: "Outfit" }} data-testid="calendar-month-label">
            {monthLabel}
          </h3>
          <button
            type="button"
            onClick={fetchAvailability}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-[var(--brand-cream-soft)] disabled:opacity-50 transition"
            aria-label="Refresh availability"
            data-testid="calendar-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => canGoNext && setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          disabled={!canGoNext}
          className="p-2 rounded-lg hover:bg-[var(--brand-cream-soft)] disabled:opacity-30 disabled:cursor-not-allowed transition"
          aria-label="Next month"
          data-testid="calendar-next"
        >
          <ChevronRight className="w-5 h-5 text-[var(--brand-teal)]" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map((date, idx) => {
          if (!date) return <div key={`b${idx}`} />;
          const iso = toIso(date);
          const state = dateState(date);
          const isSelected = selectedDate === iso;
          const dayNum = date.getDate();
          const dayTaken = taken[iso] || {};
          const openSlots = ["morning", "afternoon", "evening"].filter((s) => !dayTaken[s]).length;

          const disabled = ["past", "future", "closed", "full"].includes(state);

          return (
            <button
              key={iso}
              type="button"
              onClick={() => handleDateClick(date)}
              disabled={disabled}
              aria-label={`${date.toDateString()}${disabled ? " — unavailable" : ` — ${openSlots} of 3 slots open`}`}
              data-testid={`cal-day-${iso}`}
              className={[
                "relative aspect-square rounded-lg border text-sm font-bold transition flex flex-col items-center justify-center p-1",
                isSelected
                  ? "border-[var(--brand-teal)] bg-[var(--brand-teal)] text-white shadow-md"
                  : state === "open"
                  ? "border-[#dcd5c6] bg-white text-slate-800 hover:border-[var(--brand-teal)] hover:bg-[var(--brand-cream-soft)]"
                  : state === "partial"
                  ? "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500"
                  : state === "full"
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed line-through"
                  : state === "closed"
                  ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                  : "border-transparent bg-transparent text-slate-300 cursor-not-allowed",
              ].join(" ")}
            >
              <span>{dayNum}</span>
              {!disabled && (
                <span className="flex gap-0.5 mt-0.5">
                  {["morning", "afternoon", "evening"].map((s) => (
                    <span
                      key={s}
                      className={`w-1 h-1 rounded-full ${
                        dayTaken[s]
                          ? isSelected ? "bg-white/40" : "bg-slate-300"
                          : isSelected ? "bg-white" : "bg-[var(--brand-gold)]"
                      }`}
                    />
                  ))}
                </span>
              )}
              {state === "full" && (
                <Lock className="w-2.5 h-2.5 absolute top-0.5 right-0.5 text-slate-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] md:text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[var(--brand-gold)]" /> Open</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Partly booked</span>
        <span className="flex items-center gap-1"><Lock className="w-2.5 h-2.5 text-slate-400" /> Full</span>
        <span className="flex items-center gap-1 text-slate-400">Sundays closed</span>
      </div>

      {/* Slot picker */}
      {selectedDate && (
        <div className="mt-5 pt-5 border-t border-[#e6e0d4]" data-testid="slot-picker">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Pick a time window</div>
              <div className="text-sm font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>
                {(() => {
                  const [y, m, d] = selectedDate.split("-").map(Number);
                  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
                })()}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {["morning", "afternoon", "evening"].map((slot) => {
              const meta = SLOT_META[slot];
              const Icon = meta.icon;
              const isTaken = !!taken[selectedDate]?.[slot];
              const isSelected = selectedTime === slot;
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => handleSlotClick(slot)}
                  disabled={isTaken}
                  data-testid={`slot-${slot}`}
                  className={[
                    "px-3 py-3 rounded-lg border-2 text-left transition relative",
                    isSelected
                      ? "border-[var(--brand-teal)] bg-[var(--brand-teal)] text-white shadow-md"
                      : isTaken
                      ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "border-[#dcd5c6] bg-white text-slate-800 hover:border-[var(--brand-teal)] hover:bg-[var(--brand-cream-soft)]",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isSelected ? "text-white" : isTaken ? "text-slate-400" : "text-[var(--brand-gold)]"}`} />
                    <span className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{meta.label}</span>
                    {isTaken && <Lock className="w-3 h-3 ml-auto" />}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isSelected ? "text-white/80" : isTaken ? "text-slate-400" : "text-slate-500"}`}>
                    {meta.range}
                  </div>
                  {isTaken && (
                    <div className="text-[10px] uppercase tracking-wider font-bold mt-1 text-slate-400">Booked</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export { LOOKAHEAD_DAYS };
